<script>
import reviewService from '@services/reviewService';
import AkPictureAccommodation from '@components/v2/general/AkPictureAccommodation.vue';
import ReservationSiteConst from '@mixins/const/reservationSiteConst';
import AkButtonActionV2 from '@components/v2/general/AkButtonActionV2.vue';
import AiReplyDialog from '@views/reviews/aiReplyDialog.vue';
import UtilsMixin from '@mixins/utilsMixin';
import reviewMixin from '@mixins/reviewMixin';
import RatingStar from '@views/revenuemanagement/RatingStar.vue';
import AkShimmer from '@components/v2/general/AkShimmer.vue';
import AkOtaItemsDebug from '@components/v2/general/AkOtaItemsDebug.vue';
import Spinner from '@components/v2/general/Spinner.vue';
import AkInputTextArea from '@components/v2/input/AkInputTextArea.vue';
import AkButtonValidationV2 from '@components/v2/general/AkButtonValidationV2.vue';
import useVuelidate from '@vuelidate/core';
import {required, maxLength} from '@vuelidate/validators';
import MessageMixin from '@mixins/messageMixin';
import MandatOfferConst from '@mixins/const/mandatOfferConst';
import permissionsMixinComputed from '@mixins/permissionsMixinComputed';

export default {
  name: 'ReviewDetails',
  components: {
    AkButtonValidationV2,
    AkOtaItemsDebug,
    AkInputTextArea,
    Spinner,
    AkShimmer,
    RatingStar,
    AiReplyDialog,
    AkButtonActionV2,
    AkPictureAccommodation,
  },
  emits: ['replied'],
  mixins: [ReservationSiteConst, UtilsMixin, reviewMixin, MessageMixin, MandatOfferConst, permissionsMixinComputed],
  setup() {
    return {v$: useVuelidate()};
  },
  validations() {
    return {
      reply: {required, maxLengthValue: maxLength(2000)},
    };
  },
  data() {
    return {
      review: null,
      id: this.$route.params.reviewId,
      detailsOpen: true,
      reply: '',
    };
  },
  mounted() {
    reviewService.reviewSidebar(this.id).then((data) => {
      this.review = data;
      this.$nextTick(() => {
        const target = this.$refs.topDiv;
        const headerOffset = 80 + 32;
        const elementPosition = target.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.scrollY - headerOffset;

        window.scrollTo({
          top: offsetPosition,
        });
      });
    });
  },
  methods: {
    openAiReplyDialog() {
      this.$emit('showAiReply', this.review);
    },
    submitReply() {
      this.v$.$touch();
      if (this.v$.$errors && this.v$.$errors.length) {
        this.addError(
          this.v$.reply.required.$invalid
            ? 'Le message ne peut pas être vide.'
            : 'Le message dépasse la limite de 2000 caractères.',
        );
        this.$refs.replyBtn.init();
        return;
      }

      reviewService
        .reply(this.review.id, this.reply)
        .then((data) => {
          this.review = data;
          this.$emit('replied');
        })
        .catch((e) => {
          this.addError(e);
          this.$refs.replyBtn.init();
        });
    },
    setReply(value) {
      this.reply = value;
    },
  },
  computed: {
    reviewPeople() {
      const people = [
        {
          icon: 'ga-user_checked',
          role: 'Hôte',
          name: this.review.ownerDisplay,
          canNavigate: !this.isOwner,
          routeName: 'ownerPage',
          routeParams: {ownerId: this.review.ownerId},
        },
      ];
      if (this.review.chargeCompteId)
        people.push({
          icon: 'ga-people-speaking',
          role: 'Chargé de compte',
          name: this.review.chargeCompteDisplay,
          canNavigate: this.isGasGav,
          routeName: 'employeePage',
          routeParams: {employeeId: this.review.chargeCompteId},
        });
      if (this.review.housekeeperId)
        people.push({
          icon: 'ga-hand_key',
          role: 'Concierge partenaire',
          name: this.review.housekeeperDisplay,
          canNavigate: this.isGasGav,
          routeName: 'housekeeperPage',
          routeParams: {housekeeperId: this.review.housekeeperId},
        });

      return people;
    },
    ratingComments() {
      return this.review.ratings.filter((item) => item.comment != null);
    },
    clientFirstname() {
      if (!this.review.name) return '';
      const parts = this.review.name.split(' ');
      return parts.slice(0, -1).join(' ').toLowerCase() || parts[0].toLowerCase();
    },
    clientFirstnameCapitalized() {
      return this.clientFirstname.charAt(0).toUpperCase() + this.clientFirstname.slice(1);
    },
    validRatings() {
      return this.review.ratings.filter((item) => item.value != null);
    },
  },
};
</script>

<template>
  <div v-if="review" ref="topDiv" class="flex flex-col gap-12 mb-16 md:mb-12 px-4 md:px-0 py-6 md:py-0">
    <div class="flex flex-col gap-4">
      <div class="rounded-xl bg-secondary-50 p-2 md:pr-4 md:pb-4 pb-6 flex flex-col gap-4">
        <div class="flex flex-col md:flex-row gap-4 md:gap-6">
          <div class="md:w-[200px] w-full relative shrink-0">
            <AkPictureAccommodation
              :id="review.accommodationId"
              :md5="review.accommodationMd5"
              cover
              target-blank
              height="100%"
              class="h-[200px]"
              radius="rounded-lg" />
          </div>
          <div class="flex flex-col md:py-4 gap-4 w-full">
            <div class="flex flex-col md:gap-3 gap-4">
              <div class="flex flex-col gap-3 md:gap-2">
                <span
                  v-if="review.offer"
                  class="text-white font-medium capitalize text-[13px] px-2.5 py-0.5 rounded-full w-fit bg-secondary">
                  Formule {{ displayForMandatOfferConst(review.offer) }}
                </span>
                <span class="flex gap-1.5 items-center text-grey-900">
                  Avis N° {{ review.id }}
                  <i class="h-[3px] w-[3px] rounded-full bg-grey-900" />
                  Déposé le {{ formatDateIfNeeded(review.submittedAt) }}
                </span>
              </div>
              <div class="flex flex-col md:gap-1.5 gap-3">
                <component
                  :is="isGasGav ? 'router-link' : 'span'"
                  :to="{name: 'clientPage', params: {clientId: review.clientId}}"
                  target="_blank"
                  class="text-[28px] font-bold text-grey-900 leading-[130%]">
                  {{ review.name }}
                </component>
                <router-link
                  :to="{name: 'accommodationPage', params: {accommodationId: review.accommodationId}}"
                  target="_blank"
                  class="text-grey-900 font-bold text-md">
                  {{ review.accommodationName }}
                </router-link>
              </div>
              <span class="flex flex-wrap gap-1 items-center text-grey-900 font-medium">
                <i class="logo-site min-w-6 w-6 min-h-6 h-6" :class="iconReservation(review.reservationSite)" />
                <component
                  :is="isGasGav ? 'router-link' : 'span'"
                  :to="{name: 'reservationPage', params: {reservationId: review.reservationId}}"
                  target="_blank"
                  class="font-medium">
                  {{ review.reservationNum }}
                </component>
                <i class="h-[3px] w-[3px] rounded-full bg-grey-900" />
                <span class="shrink-0 font-medium flex items-center gap-1">
                  <i class="ga-icon ga-calendar text-md text-grey-900" />
                  {{ formatDateIfNeeded(review.reservationPeriod[0]) }} -
                  {{ formatDateIfNeeded(review.reservationPeriod[1]) }}
                </span>
              </span>
            </div>
          </div>
        </div>
        <div class="grid md:grid-flow-col gap-2 auto-cols-fr">
          <component
            :is="person.canNavigate ? 'router-link' : 'div'"
            v-for="person in reviewPeople"
            :to="{name: person.routeName, params: person.routeParams}"
            target="_blank"
            class="bg-white overflow-hidden items-center rounded-md flex gap-2 p-2 pr-3">
            <div class="bg-grey-50 min-w-[42px] h-[42px] flex items-center justify-center rounded-sm">
              <i class="ga-icon text-xl text-grey-900" :class="person.icon" />
            </div>
            <div>
              <span class="block text-grey-500">
                {{ person.role }}
              </span>
              <span class="font-semibold text-grey-900">
                {{ person.name }}
              </span>
            </div>
          </component>
        </div>
      </div>

      <div class="p-6 py-4 rounded-lg border border-grey-100 flex flex-col">
        <div class="flex flex-col md:flex-row justify-between md:items-center">
          <div class="flex gap-2 items-center">
            <span class="text-grey-900 text-xl font-semibold">Note globale</span>
            <div class="items-center flex md:gap-1 h-[42px]">
              <span
                class="font-bold text-3xl leading-6"
                :class="this.colorRatingWithRating(review.reservationSite, review.ratingDisplay)">
                {{ formatNumber(review.ratingDisplay) }}
              </span>
              <span class="text-grey-500 text-3xl leading-6">/{{ scaleRating(review.reservationSite) }}</span>
              <RatingStar
                :indicator="(review.ratingDisplay / scaleRating(review.reservationSite)) * 100"
                class="[&_.ga-icon.text-secondary]:text-[#FFAE20] w-fit ml-1.5"
                :outlined="false" />
            </div>
          </div>
          <div
            class="flex py-1 gap-1 self-end md:self-auto items-center h-fit cursor-pointer"
            @click="detailsOpen = !detailsOpen">
            <span class="text-grey-900 text-[13px] select-none font-medium">Voir le détail</span>
            <i class="ga-icon text-xl text-grey-900" :class="detailsOpen ? 'ga-chevron_up' : 'ga-chevron_down'" />
          </div>
        </div>
        <div class="detail-collapse" :class="{open: detailsOpen}">
          <div class="detail-collapse-inner flex flex-col gap-4">
            <div class="grid gap-x-2 gap-y-1 md:grid-cols-2">
              <div v-for="rating in validRatings" class="flex justify-between gap-4">
                <span class="flex gap-1.5 items-center font-medium text-grey-900">
                  {{ $t(`review.criteria.${rating.key}`) }}
                  <i v-if="rating.comment" class="ga-icon ga-comment text-md"></i>
                </span>
                <div class="flex items-center gap-3">
                  <div class="bg-grey-50 rounded-full h-1 w-[100px]">
                    <div class="bg-secondary h-full rounded-full" :style="`width: ${(rating.value * 100) / 5}px`" />
                  </div>
                  <span v-if="rating.value" class="text-sm w-5 font-semibold text-grey-900">
                    {{ formatNumber(rating.value.toFixed(1)) }}
                  </span>
                  <span v-else class="w-5"></span>
                </div>
              </div>
            </div>
            <template v-if="ratingComments.length">
              <div class="w-full h-[1px] bg-grey-100 mt-4"></div>
              <div class="flex gap-2 items-center text-md font-medium text-grey-900">
                <i class="ga-icon ga-comment text-md text-grey-900" />
                Commentaires laissés par les voyageurs
              </div>
              <div class="flex flex-col gap-1">
                <div v-for="rating in ratingComments">
                  <span class="block text-sm font-semibold text-secondary mb-0.5">
                    {{ $t(`review.criteria.${rating.key}`) }}
                  </span>
                  <span class="text-sm font-medium italic text-grey-500 leading-[160%]">{{ rating.comment }}</span>
                </div>
              </div>
            </template>
            <AkOtaItemsDebug :items="review.otaItems" />
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-8 md:px-4">
      <div class="flex items-center gap-1.5">
        <i class="ga-icon text-3xl text-grey-900 ga-comment" />
        <span class="text-grey-900 font-semibold text-xl">
          L'avis de
          <span class="capitalize text-grey-900 font-semibold text-xl">{{ clientFirstname }}</span>
        </span>
      </div>

      <div class="flex flex-col gap-2 pr-8 md:pr-[200px]">
        <div class="grid grid-flow-col gap-6 pr-6 rounded-r-md bg-secondary-50 border border-secondary w-fit">
          <div class="bg-secondary w-[7px] h-full" />
          <span class="text-grey-900 leading-[150%] py-6 whitespace-pre-line">
            {{ review.comment }}
          </span>
        </div>
        <span class="text-grey-500 font-medium text-sm">
          Avis de
          <span class="capitalize font-medium text-sm">{{ clientFirstname }},</span>
          déposé le {{ formatDateIfNeeded(review.submittedAt) }}
        </span>
      </div>

      <div v-if="review.privateComment && !isOwner" class="flex flex-col gap-2 pr-8 md:pr-[200px]">
        <div class="grid grid-flow-col w-fit">
          <div class="bg-grey-300 w-[7px] h-full" />
          <div class="flex flex-col gap-2 p-6 rounded-r-md border-dashed border-2 border-l-0 border-grey-300">
            <div class="flex items-center gap-2">
              <i class="ga-icon ga-lock text-xl" />
              <span class="text-grey-900 font-bold">Commentaire privé, seulement visible par l'équipe</span>
            </div>
            <span class="text-grey-900 leading-[150%] whitespace-pre-line">
              {{ review.privateComment }}
            </span>
          </div>
        </div>
        <span class="text-grey-500 font-medium text-sm">
          Avis privé de
          <span class="capitalize font-medium text-sm">{{ clientFirstname }},</span>
          déposé le {{ formatDateIfNeeded(review.submittedAt) }}
        </span>
      </div>

      <div v-if="review.reply" class="flex flex-col gap-2 items-end self-end pl-8 md:pl-[200px]">
        <div class="grid grid-flow-col gap-6 pl-6 rounded-l-md border border-grey-100 w-fit">
          <span class="text-grey-900 leading-[150%] py-6 whitespace-pre-line">
            {{ review.reply }}
          </span>
          <div class="bg-primary w-[7px] h-full" />
        </div>
        <span class="text-grey-500 font-medium text-sm">
          Réponse de
          <span class="capitalize font-medium text-sm">{{ review.replierDisplay }},</span>
          le {{ formatDateIfNeeded(review.dateReply) }}
        </span>
      </div>

      <template v-if="!review.reply && isGasGav">
        <div class="flex flex-col gap-2 self-end w-full md:w-[min(600px,90%)] mt-4">
          <span class="font-medium text-grey-900 text-lg">
            Répondre à
            <span class="capitalize font-medium text-grey-900 text-lg">
              {{ clientFirstname }}
            </span>
          </span>
          <!-- <div class="rounded-lg p-4 pt-2 flex flex-col gap-4 bg-jordy-50">
          <div class="flex flex-col gap-0.5">
            <div class="rounded-full bg-primary flex items-center justify-center w-4 h-4 self-end">
              <i class="ga-icon ga-supprimer text-sm font-bold text-white pb-[1px]" />
            </div>
            <span class="flex items-center gap-2 text-md font-semibold text-grey-900">
              <i class="ga-icon ga-sparkles text-xl" />
              Suggestion de l'IA
            </span>
          </div>
          <div class="flex flex-col gap-2">
            &lt;!&ndash; TODO : ajouter suggestions inline            &ndash;&gt;
            <AkButtonActionV2 label="Générer une réponse" class="!h-[48px] font-medium" @click="openAiReplyDialog" />
          </div>
        </div>-->

          <div class="border rounded-md border-grey-100 replyZone">
            <div class="flex flex-col p-4 pb-1">
              <AkInputTextArea
                :validator="v$.reply"
                class-name="grow h-fit min-h-[64px]"
                v-model="reply"
                :placeholder="`Bonjour ${clientFirstnameCapitalized}, merci beaucoup pour votre retour...`" />
              <span
                class="leading-[36px] text-sm self-end"
                :class="{
                  'text-grey-500': reply.length < 1800,
                  'text-orange-500': reply.length >= 1800 && reply.length <= 2000,
                  'text-red-500': reply.length > 2000,
                }">
                {{ reply.length }}/2000
              </span>
            </div>
            <div class="flex p-2 justify-end items-center border-t border-grey-100">
              <div class="flex gap-2">
                <div
                  class="cursor-pointer bg-secondary-50 hover:bg-secondary-100 h-[48px] aspect-square flex items-center justify-center rounded-md"
                  @click="openAiReplyDialog">
                  <i class="ga-icon ga-sparkles text-xl text-secondary" />
                </div>
                <AkButtonValidationV2
                  ref="replyBtn"
                  label="Répondre"
                  class="!px-3 !h-[48px] !text-[13px] font-medium"
                  @click="submitReply" />
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
  <div v-else class="h-[calc(100vh_-_80px_-_52px)] w-full flex flex-col gap-2 items-center justify-center">
    <Spinner />
    <span class="text-sm text-grey-500">Chargement en cours...</span>
  </div>
</template>

<style scoped>
.logo-site {
  background-size: 100%;
}

:deep(.picture-accommodation) {
  aspect-ratio: unset;
  height: 100%;
}

.detail-collapse {
  display: grid;
  grid-template-rows: 0fr;
  padding: 0;
  transition:
    grid-template-rows 0.3s ease,
    padding 0.3s ease;
}
.detail-collapse.open {
  grid-template-rows: 1fr;
  @apply py-2;
}
.detail-collapse-inner {
  overflow: hidden;
}

.replyZone :deep(textarea) {
  @apply border-none !bg-transparent resize-y text-md p-0 !rounded-none h-[200px] min-h-[64px] max-h-[400px];

  &::placeholder {
    @apply text-md;
  }
}
</style>
